import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  SERVICE_INSTALL_PLUS_TYPE_IDS,
  VALID_CARE_PLANS,
} from "@src/constants";

import Autocomplete from "@mui/material/Autocomplete";
import { CacheTypes } from "@utils/cache/interfaces";
import GenericPopupModal from "@src/components/Shared/GenericPopupModal"; // Adjust the import path as necessary
import Grid from "@mui/material/Grid2";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { cacheUtils } from "@src/index"; // Adjust the import path as necessary
import { isGasBoilerServiceJob } from "../../../RatesTab/DiscountsTab/discountsLogic";
import { ui } from "@src/interfaces/interfaces";
import { useAppSelector } from "@store/hooks";
import { useGetAvailabilityForCurrentJobAddress } from "@src/hooks/useCheckAval"; // Adjust the import path as necessary
import useGetAvailableOffers from "@src/hooks/useGetAvailableOffers"; // Adjust the import path as necessary
import useRecalculatePayments from "@src/hooks/useRecalculatePayments"; // Adjust the import path as necessary

/**
 * Props interface for the Notes component.
 */
type NotesProps = {
  /**
   * The current note value.
   */
  note: string;

  /**
   * Handler function for note changes.
   */
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;

  /**
   * The name attribute for the note input field.
   */
  name: string;
};

/**
 * Notes Component
 * Renders a multiline TextField for policy notes.
 *
 * @param {NotesProps} props - The props for the Notes component.
 * @returns {React.FC} The Notes component.
 */
const Notes: React.FC<NotesProps> = ({ note, handleChange, name }) => (
  <Grid
    size={{
      xs: 12,
      sm: 12,
    }}
  >
    <TextField
      onChange={handleChange}
      id="filled-multiline-static"
      label="Policy notes"
      multiline
      rows={4}
      name={name}
      value={note}
      variant="outlined"
      fullWidth
    />
  </Grid>
);

/**
 * Handles the change event for the Plan Type Autocomplete.
 *
 * @param {React.SyntheticEvent<Element, Event>} event - The event object.
 * @param {any} value - The selected value from the Autocomplete.
 */
const handleType = (
  event: React.SyntheticEvent<Element, Event>,
  value: any,
) => {
  ui.CarePlanPlanDetailsPlanOptionsPlanType = value;
};

/**
 * Handles the change event for the Duration Autocomplete.
 *
 * @param {React.SyntheticEvent<Element, Event>} event - The event object.
 * @param {any} value - The selected value from the Autocomplete.
 */
const handleDuration = (
  event: React.SyntheticEvent<Element, Event>,
  value: any,
) => {
  ui.CarePlanPlanDetailsPlanOptionsDuration = value;
};

/**
 * Handles the change event for the Billing Frequency Autocomplete.
 *
 * @param {React.SyntheticEvent<Element, Event>} event - The event object.
 * @param {any} value - The selected value from the Autocomplete.
 */
const handleFrequency = (
  event: React.SyntheticEvent<Element, Event>,
  value: any,
) => {
  ui.CarePlanPlanDetailsPlanOptionsBillingFrequency = value;
};

/**
 * Handles the change event for the Excess Autocomplete.
 *
 * @param {React.SyntheticEvent<Element, Event>} event - The event object.
 * @param {any} value - The selected value from the Autocomplete.
 */
const handleExcess = (
  event: React.SyntheticEvent<Element, Event>,
  value: any,
) => {
  ui.CarePlanPlanDetailsPlanOptionsExcess = value;
};

/**
 * Handles the change event for the Comments TextField.
 *
 * @param {React.ChangeEvent<HTMLInputElement>} event - The change event object.
 */
const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  const value = event.target.value ? event.target.value : "";
  ui.CarePlanPlanDetailsPlanOptionsComments = value;
};

/**
 * Props interface for the PlanOptionsTab component.
 */
type Props = {};

/**
 * PlanOptionsTab Component
 * Renders the Plan Options tab with various Autocomplete fields and policy notes.
 *
 * @param {Props} props - The props for the PlanOptionsTab component.
 * @returns {React.FC} The PlanOptionsTab component.
 */
const PlanOptionsTab: React.FC<Props> = () => {
  /**
   * Prefix used for naming input fields.
   */
  const prefix = "CarePlanPlanDetailsPlanOptions";

  /**
   * Selectors to retrieve necessary state values from the Redux store.
   */
  const policyComments = useAppSelector(
    ({ ui }) => ui.CarePlanPlanDetailsPlanOptionsComments,
  );
  const planTypeHelperText = useAppSelector(
    ({ ui }) => ui.CarePlanPlanDetailsPlanOptionsPlanTypeHelperText,
  );
  const planType = useAppSelector(
    ({ ui }) => ui.CarePlanPlanDetailsPlanOptionsPlanType,
  );
  const durationsHelperText = useAppSelector(
    ({ ui }) => ui.CarePlanPlanDetailsPlanOptionsDurationHelperText,
  );
  const duration = useAppSelector(
    ({ ui }) => ui.CarePlanPlanDetailsPlanOptionsDuration,
  );
  const billingFrequencyHelperText = useAppSelector(
    ({ ui }) => ui.CarePlanPlanDetailsPlanOptionsBillingFrequencyHelperText,
  );
  const frequency = useAppSelector(
    ({ ui }) => ui.CarePlanPlanDetailsPlanOptionsBillingFrequency,
  );
  const excessHelperText = useAppSelector(
    ({ ui }) => ui.CarePlanPlanDetailsPlanOptionsExcessHelperText,
  );
  const excesses = useAppSelector(
    ({ ui }) => ui.CarePlanPlanDetailsPlanOptionsExcess,
  );
  const carePlanSummary = useAppSelector(({ ui }) => ui.CarePlanSummary);
  const carePlanPlanTypeValid = useAppSelector(
    ({ state }) => state.CarePlanPlanDetailsPlanOptionsPlanTypeValid,
  );
  const carePlanPaymentFrequencyValid = useAppSelector(
    ({ state }) => state.CarePlanPlanDetailsPlanOptionsBillingFrequencyValid,
  );
  const carePlanDurationValid = useAppSelector(
    ({ state }) => state.CarePlanPlanDetailsPlanOptionsDurationValid,
  );
  const carePlanExcessValid = useAppSelector(
    ({ state }) => state.CarePlanPlanDetailsPlanOptionsExcessValid,
  );
  const genericPopupModal = useAppSelector(({ ui }) => ui.GenericPopupModal);
  const isFixedPrice = useAppSelector(({ state }) => state.IsFixedPrice);
  const jobTypeCode = useAppSelector(({ state }) => state.JobTypeCode);

  /**
   * Ref to track if the generic popup modal has been shown.
   */
  const hasShownModal = useRef(false);

  /**
   * Determines if the selected duration is 18 months.
   */
  const is18Months = duration?.serviceAgreementDurationId === 18;

  /**
   * State to determine if the selected plan is a Landlord plan.
   */
  const [isLandlordPlan, setIsLandlordPlan] = useState(false);

  /**
   * State to determine if the selected plan is Service+ or Install+
   */
  const [isServiceInstallPlusPlan, setIsServiceInstallPlusPlan] =
    useState(false);

  /**
   * Determines if the job type is Gas Boiler Service.
   */
  const isJobTypeGasBoilerService = useMemo(() => {
    return isGasBoilerServiceJob(jobTypeCode);
  }, [jobTypeCode]);

  /**
   * Custom hooks to manage side effects.
   */
  useRecalculatePayments();
  useGetAvailableOffers();
  useGetAvailabilityForCurrentJobAddress();

  /**
   * Effect to set the Landlord plan or Service/Install Plus plan status based on the selected plan type.
   */
  useEffect(() => {
    const serviceInstallPlusIdFound = SERVICE_INSTALL_PLUS_TYPE_IDS.includes(
      planType?.serviceAgreementTypeId || 0,
    );
    setIsServiceInstallPlusPlan(serviceInstallPlusIdFound);
    setIsLandlordPlan(
      (!serviceInstallPlusIdFound &&
        (planType?.isLandlord || planType?.name?.includes("Landlord"))) ??
        false,
    );
  }, [planType]);

  /**
   * Effect to set the Excess option based on the Service/Install Plus plan or Landlord plan status.
   * Effect also sets the Duration option based on the Service/Install Plus plan status.
   */
  useEffect(() => {
    const updateDefaultExcess = (
      isServiceInstallPlusType: boolean,
      isLandlordType: boolean,
    ) => {
      // For Service+ and Install+ plans, the excess should always be 0
      if (isServiceInstallPlusType)
        ui.CarePlanPlanDetailsPlanOptionsExcess = cacheUtils
          .getCache<CacheTypes.ServiceAgreementExcess>(
            "ServiceAgreementExcesses",
          )
          ?.find((e) => e.excess === 0);
      // For landlord plans, there is no 0 excess, so the default should be 75
      else if (isLandlordType && excesses?.serviceAgreementExcessId === 0)
        ui.CarePlanPlanDetailsPlanOptionsExcess = cacheUtils
          .getCache<CacheTypes.ServiceAgreementExcess>(
            "ServiceAgreementExcesses",
          )
          ?.find((e) => e.excess === 75);
    };
    const updateDefaultDuration = (isServiceInstallPlusType: boolean) => {
      if (isServiceInstallPlusType)
        ui.CarePlanPlanDetailsPlanOptionsDuration = cacheUtils
          .getCache<CacheTypes.ServiceAgreementDuration>(
            "ServiceAgreementDurations",
          )
          ?.find((d) => d.serviceAgreementDurationId === 12);
    };
    updateDefaultExcess(isServiceInstallPlusPlan, isLandlordPlan);
    updateDefaultDuration(isServiceInstallPlusPlan);
  }, [isLandlordPlan, isServiceInstallPlusPlan, excesses, planType]);

  /**
   * Effect to display the generic popup modal when triggered.
   */
  useEffect(() => {
    if (!hasShownModal.current && genericPopupModal) {
      // Code to show modal can be implemented here
      hasShownModal.current = true; // Update the ref to indicate modal has been shown
    }
  }, [genericPopupModal]);

  /**
   * Effect to display a specific modal based on billing frequency and duration.
   */
  useEffect(() => {
    if (
      frequency?.serviceAgreementBillingFrequencyId &&
      frequency.serviceAgreementBillingFrequencyId > 3 &&
      duration?.serviceAgreementDurationId === 18
    ) {
      if (!hasShownModal.current) {
        ui.GenericPopupModal = true;
        hasShownModal.current = true;
      }
    }
  }, [duration, frequency]);

  /**
   * Memoized options for Plan Types to optimize performance.
   */
  const planTypeOptions = useMemo(() => {
    const plans = cacheUtils.getCache<CacheTypes.ServiceAgreementTypes>(
      "ServiceAgreementTypes",
    );
    console.log("plan Types", plans);
    return cacheUtils.getCache<CacheTypes.ServiceAgreementTypes>(
      "ServiceAgreementTypes",
    );
  }, []);

  /**
   * Memoized options for Service Agreement Durations.
   */
  const durationOptions = useMemo(() => {
    return isServiceInstallPlusPlan
      ? cacheUtils
          .getCache<CacheTypes.ServiceAgreementDuration>(
            "ServiceAgreementDurations",
          )
          ?.filter((d) => d.serviceAgreementDurationId === 12)
      : cacheUtils.getCache<CacheTypes.ServiceAgreementDuration>(
          "ServiceAgreementDurations",
        );
  }, [isServiceInstallPlusPlan]);

  /**
   * Memoized options for Billing Frequencies.
   */
  const billingFrequencyOptions = useMemo(() => {
    return cacheUtils.getCache<CacheTypes.ServiceAgreementBillingFrequency>(
      "ServiceAgreementBillingFrequencies",
    );
  }, []);

  /**
   * Memoized options for Service Agreement Excesses.
   */
  const excessOptions = useMemo(() => {
    return isServiceInstallPlusPlan
      ? cacheUtils
          .getCache<CacheTypes.ServiceAgreementExcess>(
            "ServiceAgreementExcesses",
          )
          ?.filter((e) => e.serviceAgreementExcessId === 0)
      : isLandlordPlan
        ? cacheUtils
            .getCache<CacheTypes.ServiceAgreementExcess>(
              "ServiceAgreementExcesses",
            )
            ?.filter((e) => e.serviceAgreementExcessId !== 0)
        : cacheUtils.getCache<CacheTypes.ServiceAgreementExcess>(
            "ServiceAgreementExcesses",
          );
  }, [isServiceInstallPlusPlan, isLandlordPlan]);

  /**
   * Memoized Plan Type Label based on plan properties.
   */
  const getPlanTypeLabel = useCallback(
    (options: CacheTypes.ServiceAgreementTypes) => {
      const isSpecialPlanType =
        VALID_CARE_PLANS.includes(options.serviceAgreementTypeId ?? 0) &&
        isJobTypeGasBoilerService;
      let label = isFixedPrice
        ? `${options.name} - 10% discount on Job Fixed Price`
        : `${options.name} - 10% discount on Job Rate`;

      if (isSpecialPlanType && isFixedPrice && is18Months) {
        label = `${options.name} - Fixed Price of £69 with Boiler Service`;
      }

      return label;
    },
    [isJobTypeGasBoilerService, isFixedPrice, is18Months],
  );

  /**
   * Memoized Plan Type Render Option to apply special styling.
   */
  const renderPlanTypeOption = useCallback(
    (
      props: React.HTMLAttributes<HTMLSpanElement>,
      option: CacheTypes.ServiceAgreementTypes,
    ) => {
      const isSpecialPlanType = VALID_CARE_PLANS.includes(
        option.serviceAgreementTypeId ?? 0,
      );
      const specialHighlight =
        isSpecialPlanType &&
        isFixedPrice &&
        is18Months &&
        isJobTypeGasBoilerService
          ? { backgroundColor: "#ffc8006e" }
          : {};

      let label = isFixedPrice
        ? `${option.name} - 10% discount on Fixed Price`
        : `${option.name} - 10% discount on Job Rate`;

      if (
        isSpecialPlanType &&
        isFixedPrice &&
        is18Months &&
        isJobTypeGasBoilerService
      ) {
        label = `${option.name} - Job Fixed Price of £69 with Boiler Service`;
      }

      return (
        <span
          {...props}
          key={option.serviceAgreementTypeId}
          style={specialHighlight}
        >
          {label}
        </span>
      );
    },
    [isFixedPrice, is18Months, isJobTypeGasBoilerService],
  );

  return (
    <Grid container spacing={2}>
      {/* Plan Type Autocomplete */}
      <Grid
        size={{
          xs: 12,
          sm: 12,
        }}
      >
        <Autocomplete
          id="combo-box-planTypes"
          options={planTypeOptions}
          isOptionEqualToValue={(option, value) =>
            option.serviceAgreementTypeId === value.serviceAgreementTypeId
          }
          onChange={handleType}
          value={planType?.serviceAgreementTypeId ? planType : null}
          renderInput={(params) => (
            <TextField
              required
              {...params}
              label="Plan Type"
              error={!carePlanPlanTypeValid}
              name={`${prefix}PlanType`}
              helperText={!carePlanPlanTypeValid && planTypeHelperText}
            />
          )}
          getOptionLabel={getPlanTypeLabel}
          renderOption={renderPlanTypeOption}
        />
      </Grid>
      {/* Plan Type Description */}
      {planType?.description && (
        <Grid
          size={{
            xs: 12,
            sm: 12,
          }}
        >
          <Paper elevation={4} sx={{ p: 1 }}>
            <Typography variant="body1">{planType.description}</Typography>
          </Paper>
        </Grid>
      )}
      {/* Duration Autocomplete */}
      {!isServiceInstallPlusPlan && (
        <Grid
          size={{
            xs: 12,
            sm: 12,
          }}
        >
          <Autocomplete
            id="combo-box-billing-duration"
            options={durationOptions}
            getOptionLabel={(option) => String(option.name)}
            isOptionEqualToValue={(option, value) =>
              option.serviceAgreementDurationId ===
              value.serviceAgreementDurationId
            }
            onChange={handleDuration}
            value={duration ?? null}
            renderInput={(params) => (
              <TextField
                required
                {...params}
                label="Duration"
                name={`${prefix}Duration`}
                error={!carePlanDurationValid}
                helperText={!carePlanDurationValid && durationsHelperText}
              />
            )}
          />
        </Grid>
      )}
      {/* Billing Frequency Autocomplete */}
      <Grid
        size={{
          xs: 12,
          sm: 12,
        }}
      >
        <Autocomplete
          id="combo-box-billing-frequency"
          options={billingFrequencyOptions}
          getOptionLabel={(option) => String(option.name)}
          isOptionEqualToValue={(option, value) =>
            option.serviceAgreementBillingFrequencyId ===
            value.serviceAgreementBillingFrequencyId
          }
          onChange={handleFrequency}
          value={frequency ?? null}
          renderInput={(params) => (
            <TextField
              required
              {...params}
              label="Billing Frequency"
              name={`${prefix}BillingFrequency`}
              error={!carePlanPaymentFrequencyValid}
              helperText={
                !carePlanPaymentFrequencyValid && billingFrequencyHelperText
              }
            />
          )}
        />
      </Grid>
      {/* Excess Autocomplete */}
      {!isServiceInstallPlusPlan && (
        <Grid
          size={{
            xs: 12,
            sm: 12,
          }}
        >
          <Autocomplete
            id="combo-box-billing-excess"
            options={excessOptions}
            getOptionLabel={(option) => String(option.excess)}
            isOptionEqualToValue={(option, value) =>
              option.serviceAgreementExcessId === value.serviceAgreementExcessId
            }
            onChange={handleExcess}
            value={excesses ?? null}
            renderInput={(params) => (
              <TextField
                required
                {...params}
                label="Excess"
                name={`${prefix}Excess`}
                error={!carePlanExcessValid}
                helperText={!carePlanExcessValid && excessHelperText}
              />
            )}
          />
        </Grid>
      )}
      {/* Care Plan Summary */}
      {carePlanSummary && (
        <Grid
          size={{
            xs: 12,
            sm: 12,
          }}
        >
          <Paper elevation={4} sx={{ p: 1 }}>
            <Typography variant="body1">{carePlanSummary}</Typography>
          </Paper>
        </Grid>
      )}
      {/* Policy Notes */}
      <Grid
        size={{
          xs: 12,
          sm: 12,
        }}
      >
        <Notes
          note={policyComments}
          handleChange={handleChange}
          name={`${prefix}Comments`}
        />
      </Grid>
      {/* Generic Popup Modal */}
      {hasShownModal.current && (
        <GenericPopupModal
          modalTitle="Attention Required"
          adviseList={[
            "18 months billing cycle is only available through Direct Debit, monthly and quarterly are supported too!",
          ]}
          buttonText="Close Message"
        />
      )}
    </Grid>
  );
};

export default PlanOptionsTab;
